import { Component, inject, input, OnInit, output, signal } from '@angular/core';
import { AsyncPipe } from '@angular/common';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { finalize, forkJoin, Observable, tap } from 'rxjs';

// Components 
import { TableComponent } from "../../../shared/components/table/table.component";
import { UrlPatternEditModalComponent } from '../url-pattern-edit-modal/url-pattern-edit-modal.component';
import { VerifyUrlModalComponent } from '../verify-url-modal/verify-url-modal.component';
import { ConfirmModal } from '../../../shared/components/confirm-modal/confirm-modal.component';
import { CheckUrlModalComponent } from '../check-url-modal/check-url-modal.component';
import { AddSitemapModalComponent } from '../add-sitemap-modal/add-sitemap-modal.component';

// Models
import { Column } from '../../../shared/models/column.model';
import { MultiAction } from '../../../shared/models/multi-action.model';
import { PaginatedUrlsResponse, Pagination } from '../../../shared/models/pagination.model';
import { Sitemap } from '../../models/sitemap.model';

// Services
import { UrlService } from '../../services/url.service';
import { UrlPattern } from '../../models/url-pattern.model';
import { ToastService } from '../../../shared/services/toast.service';


@Component({
  selector: 'app-url-pattern-list',
  imports: [AsyncPipe, TableComponent],
  templateUrl: './url-pattern-list.component.html'
})
export class UrlPatternListComponent implements OnInit {
  private readonly modalSrv = inject(NgbModal);
  private readonly urlSrv = inject(UrlService);
  private readonly toastSrv = inject(ToastService);

  readonly contextId = input.required<string>();
  docNumber = output<number>();

  multiActions: MultiAction[] = [
    { label: $localize`Controlla Siti Web`, action: 'check', buttonType: 'secondary' },
    { label: $localize`Aggiungi Sito Web`, action: 'add', buttonType: 'primary' },
    { label: $localize`Elimina Siti Web`, action: 'delete', buttonType: 'danger' }
  ];

  urlColumns: Column[] = [
    { label: 'Id', key: 'id', type: 'hidden' },
    { label: $localize`Siti Web`, key: 'pattern', type: 'string', fixedSize: true },
    { label: $localize`Incluso/Escluso`, key: 'patternType', type: 'inclusion' },
    { label: $localize`Stato`, key: 'status', type: 'status' },
    { label: $localize`Ultima Modifica`, key: 'updateTime', type: 'date' }
  ];

  pagination: Pagination = {
    page: 1,
    pageSize: 30,
    sortColumn: '',
    sortDirection: ''
  };

  sitemaps = signal<Sitemap[]>([]);

  urls$?: Observable<PaginatedUrlsResponse<UrlPattern>>;
  loading: boolean = false;
  sitemapLoading = false;

  ngOnInit(): void {
    this.loadData();
    this.loadSitemaps();
  }

  updatePagination(newPage: number): void {
    this.pagination.page = newPage;
    this.loadData();
  }

  updateSort(sortEvent: { column: string, direction: string }): void {
    this.pagination.sortColumn = sortEvent.column;
    this.pagination.sortDirection = sortEvent.direction;
    this.loadData();
  }

  loadData(searchTerm: string = ''): void {
    this.loading = true;
    this.urls$ = this.urlSrv.getStartUrlsPaginated(this.contextId(), this.pagination).pipe(
      tap((urls) => {
        this.docNumber.emit(urls ? urls.documentNumber : 0);
      }),
      finalize(() => {
        this.loading = false;
      })
    );
  }

  loadSitemaps() {
    this.sitemapLoading = true;

    this.urlSrv.getSitemaps(this.contextId()).subscribe({
      next: (data) => {
        this.sitemaps.set(data);
        this.sitemapLoading = false;
      },
      error: () => {
        this.sitemapLoading = false;
      }
    });
  }

  openNewModal() {
    const buttonElement = document.activeElement as HTMLElement; // Get the currently focused element
    buttonElement.blur(); // Remove focus from the button
    const modalRef = this.modalSrv.open(UrlPatternEditModalComponent, { centered: true, windowClass: 'qai-modal', size: 'lg' });
    modalRef.componentInstance.contextId = this.contextId;
    modalRef.result.then(
      (result) => {
        console.log(`Closed with: ${result}`);
        this.loadData();
        setTimeout(() => {
          this.addSitemap(); // Open the Add Sitemap modal immediately after closing the URL Pattern Edit modal
        }, 1000);
      }
    );
  }

  deleteUrls(urlList: UrlPattern[]) {
    const modalRef = this.modalSrv.open(ConfirmModal, { centered: true, windowClass: 'qai-modal' });
    modalRef.componentInstance.title = 'Elimina Siti Web';
    modalRef.componentInstance.body = 'Stai eliminando i seguenti Siti Web:';
    modalRef.componentInstance.list = urlList.map(el => el.pattern);
    modalRef.result.then(
      (result) => {
        console.log(`Closed with: ${result}`);
        this.apiDeleteUrls(urlList);
      }
    );
  }

  openVerifyModal() {
    const modalRef = this.modalSrv.open(VerifyUrlModalComponent, { centered: true, windowClass: 'qai-modal', size: 'lg' });
    modalRef.result.then(
      (result) => {
        console.log(`Closed with: ${result}`);
        this.apiReloadIndexing();
      }
    );
  }

  openCheckModal() {
    const modalRef = this.modalSrv.open(CheckUrlModalComponent, { centered: true, windowClass: 'qai-modal', size: 'lg' });
    modalRef.componentInstance.contextId = this.contextId;
    modalRef.result.then(
      (result) => {
        console.log(`Closed with: ${result}`);
        this.loadData();
      }
    );
  }

  removeSitemap(id: string) {
    const confirmModal = this.modalSrv.open(ConfirmModal, { centered: true, windowClass: 'qai-modal' });
    confirmModal.componentInstance.title = 'Elimina Sitemap';
    confirmModal.componentInstance.body = 'Stai eliminando la seguente Sitemap:';
    confirmModal.componentInstance.list = [this.sitemaps().find(s => s.id === id)?.sitemap || ''];
    confirmModal.result.then(
      (result) => {
        console.log(`Closed with: ${result}`);
        this.apiDeleteSitemap(id);
      }
    );
  }

  addSitemap() {
    const modalRef = this.modalSrv.open(AddSitemapModalComponent, { centered: true, windowClass: 'qai-modal', size: 'lg' });
    modalRef.componentInstance.contextId = this.contextId;
    modalRef.result.then(
      (result) => {
        console.log(`Closed with: ${result}`);
        this.loadSitemaps();
      }
    );
  }

  private apiReloadIndexing() {
    this.urlSrv.refreshVerification(this.contextId()).subscribe({
      next: () => {
        this.toastSrv.show({ type: 'success', message: $localize`Pattern Url verificate con successo` });
        this.loadData();  // Refresh the list after deletion
      }
    });
  }

  private apiDeleteUrls(urlList: UrlPattern[]) {
    console.log(urlList);

    const deleteRequests = urlList.map(group => this.urlSrv.deleteStartUrlById(this.contextId(), group.id));

    if (deleteRequests.length > 0) {
      forkJoin(deleteRequests).subscribe({
        next: () => {
          this.toastSrv.show({ type: 'success', message: $localize`Pattern Url eliminate con successo` });
          this.loadData();  // Refresh the list after deletion
        }
      });
    } else {
      console.warn('No valid IDs provided for deletion.');
    }
  }

  private apiDeleteSitemap(id: string) {
    this.urlSrv.deleteSitemap(this.contextId(), id.toString()).subscribe({
      next: () => {
        this.toastSrv.show({ type: 'success', message: $localize`Sitemap eliminata con successo` });
        this.loadSitemaps();
      }
    });
  }
}
